(() => {
  'use strict';

  const splitSources = (value) => String(value || '').split('||').map((v) => v.trim()).filter(Boolean);
  const text = (value) => String(value ?? '').trim();
  const normalize = (value) => text(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('nl-NL');
  const el = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined && content !== null) node.textContent = content;
    return node;
  };
  const appendField = (dl, label, value, fallback) => {
    const wrap = el('div', 'dcv2-field');
    wrap.append(el('dt', '', label), el('dd', '', text(value) || fallback));
    dl.append(wrap);
  };
  const statusClass = (status) => {
    const s = normalize(status);
    if (s.includes('gecertificeerd') || s.includes('erkend') || s.includes('gekwalificeerd')) return 'certified';
    if (s.includes('formeel') || s.includes('behandeling')) return 'review';
    if (s.includes('inhoudelijk')) return 'substantive';
    if (s.includes('pilot') || s.includes('meeting')) return 'pilot';
    if (s.includes('doorverwezen')) return 'referred';
    if (s.includes('vervolgstap') || s.includes('delivery') || s.includes('re-verification')) return 'action';
    if (s.includes('ontvangst')) return 'received';
    return 'contacted';
  };
  const fetchRecords = async (sources) => {
    if (!sources.length) throw new Error('Geen databronnen opgegeven.');
    const parts = await Promise.all(sources.map(async (source) => {
      const response = await fetch(source, { credentials: 'same-origin', cache: 'no-cache' });
      if (!response.ok) throw new Error(`Dataset kon niet worden geladen (${response.status}).`);
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Dataset heeft een ongeldig formaat.');
      return data;
    }));
    const seen = new Set();
    const valid = [];
    parts.flat().forEach((record) => {
      const id = text(record?.i);
      const name = text(record?.n);
      if (!id || !name || seen.has(id)) return;
      seen.add(id);
      valid.push(record);
    });
    return valid;
  };

  const createRouteCard = (record, fallbackRelevance, verified) => {
    const id = text(record.i);
    const article = el('article', 'dcv2-route-card');
    article.id = id.toLocaleLowerCase('nl-NL');
    article.dataset.dcv2Route = '';
    article.dataset.dcv2Search = [record.i, record.n, record.j, record.w, record.c, record.s, record.d, record.q, record.r, record.a, record.x].map(text).filter(Boolean).join(' ');

    const head = el('div', 'dcv2-route-head');
    const titleWrap = el('div');
    titleWrap.append(el('div', 'dcv2-route-meta', `${id} · ${text(record.j) || 'Jurisdictie niet publiek vastgelegd'} · ${text(record.w) || 'Wereldregio niet publiek vastgelegd'}`));
    titleWrap.append(el('h2', '', text(record.n)));
    const badge = el('span', `dcv2-status dcv2-status--${statusClass(record.s)}`, text(record.s) || 'STATUS NIET VASTGELEGD');
    head.append(titleWrap, badge);
    article.append(head);
    article.append(el('p', '', text(record.c) || fallbackRelevance));

    const details = el('details');
    details.append(el('summary', '', 'Bekijk bewijsdossier'));
    const dl = el('dl', 'dcv2-dossier');
    appendField(dl, 'Officiële naam', record.n, 'Niet publiek vastgelegd.');
    appendField(dl, 'Land / regio / internationaal', record.j, 'Niet publiek vastgelegd.');
    appendField(dl, 'Wereldregio', record.w, 'Niet afzonderlijk vastgelegd.');
    appendField(dl, 'Categorie', record.c, 'Niet afzonderlijk vastgelegd.');
    appendField(dl, 'Waarom relevant', fallbackRelevance, 'Relevantie niet afzonderlijk vastgelegd.');
    appendField(dl, 'Geopende route / vraag', record.q, 'Niet afzonderlijk publiek vastgelegd in het huidige canonieke record.');
    appendField(dl, 'Datum eerste benadering', record.f, 'Nog niet verifieerbaar in de publieke routedataset.');
    appendField(dl, 'Actuele status', record.s, 'Niet publiek vastgelegd.');
    appendField(dl, 'Bewijsstatus / reactie', record.d, 'Nog geen sterkere status dan de canonieke routeopening gepubliceerd.');
    appendField(dl, 'Publieke ticket-/referentiecode', record.r, 'Geen publiek geschikte referentiecode vastgelegd.');
    appendField(dl, 'Institutioneel publiek adres', record.a, 'Nog niet brongeverifieerd in deze publieke dataset; daarom niet ingevuld.');
    appendField(dl, 'Volgende stap', record.x, 'Volg de actuele bewijs- en responsroute volgens het masterregister.');
    appendField(dl, 'Laatste verificatie', record.v || verified, verified || 'Niet vastgelegd.');
    appendField(dl, 'Wat hiermee níét bewezen is', '', 'Geen partnerschap, endorsement, certificering, accreditatie of wettelijke erkenning aangetoond, tenzij dat later met direct onafhankelijk bewijs expliciet wordt vastgelegd.');
    appendField(dl, 'Bewijsbron', '', 'WORLD LAUNCH CAMPAIGN REGISTER, gereconcilieerd met geverifieerde directe correspondentie; ruwe privé-correspondentie wordt niet standaard gepubliceerd.');
    details.append(dl);
    article.append(details);
    return article;
  };

  const initDomain = async (root) => {
    const list = root.querySelector('[data-dcv2-route-list]');
    const input = root.querySelector('[data-dcv2-filter]');
    const status = root.querySelector('[data-dcv2-filter-status]');
    const empty = root.querySelector('[data-dcv2-no-results]');
    const countNode = root.querySelector('[data-dcv2-domain-count]');
    const sources = splitSources(root.dataset.dcv2Sources);
    const relevance = text(root.dataset.dcv2Relevance) || 'Relevantie voor onafhankelijke toetsing binnen dit domein.';
    const verified = text(root.dataset.dcv2Verified);
    let records = [];

    const apply = () => {
      const query = normalize(input?.value);
      let shown = 0;
      root.querySelectorAll('[data-dcv2-route]').forEach((card) => {
        const hit = !query || normalize(card.dataset.dcv2Search || card.textContent).includes(query);
        card.hidden = !hit;
        if (hit) shown += 1;
      });
      if (status) status.textContent = query ? `${shown} van ${records.length} instanties zichtbaar` : `${records.length} instanties zichtbaar`;
      if (empty) empty.hidden = shown !== 0;
    };

    try {
      records = await fetchRecords(sources);
      if (!records.length) throw new Error('Geen geldige canonieke records gevonden.');
      const fragment = document.createDocumentFragment();
      records.forEach((record) => fragment.append(createRouteCard(record, relevance, verified)));
      list.replaceChildren(fragment);
      list.setAttribute('aria-busy', 'false');
      if (countNode) countNode.textContent = String(records.length);
      input?.addEventListener('input', apply, { passive: true });
      apply();
    } catch (error) {
      list.replaceChildren();
      const failure = el('div', 'dcv2-boundary');
      failure.append(el('strong', '', 'Bewijsdossiers konden niet worden geladen.'), document.createElement('br'), document.createTextNode('Ververs de pagina. Een laadfout verandert geen bewijsstatus en creëert geen nieuwe claim.'));
      list.append(failure);
      list.setAttribute('aria-busy', 'false');
      if (status) status.textContent = 'Dossiers tijdelijk niet geladen';
    }
  };

  const initHub = async (root) => {
    const sources = splitSources(root.dataset.dcv2Sources);
    const status = root.querySelector('[data-dcv2-hub-status]');
    try {
      const records = await fetchRecords(sources);
      if (!records.length) throw new Error('Geen geldige canonieke records gevonden.');
      const statuses = new Map();
      const jurisdictions = new Set();
      const regions = new Map();
      const domains = new Map();
      records.forEach((record) => {
        const s = text(record.s);
        statuses.set(s, (statuses.get(s) || 0) + 1);
        if (text(record.j)) jurisdictions.add(text(record.j));
        const region = text(record.w) || 'International / Global';
        regions.set(region, (regions.get(region) || 0) + 1);
        const domain = text(record.c);
        domains.set(domain, (domains.get(domain) || 0) + 1);
      });
      const received = records.filter((record) => normalize(record.s) === 'ontvangst bevestigd').length;
      const substantive = records.filter((record) => normalize(record.s) === 'inhoudelijk antwoord ontvangen').length;
      const active = records.filter((record) => /in behandeling|doorverwezen|vervolgstap vereist|pilot-\/onderzoeksverkenning/i.test(text(record.s))).length;
      const pilot = records.filter((record) => normalize(record.s).includes('pilot-/onderzoeksverkenning')).length;
      const standards = records.filter((record) => ['Normalisatie & standaarden','Accreditatie & certificatieschema’s'].includes(text(record.c))).length;
      const formal = records.filter((record) => /formele aanvraag|gekwalificeerd|gecertificeerd|wettelijk\/regulatoir erkend/i.test(text(record.s))).length;
      root.querySelectorAll('[data-dcv2-metric="routes"]').forEach((node) => { node.textContent = String(records.length); });
      root.querySelectorAll('[data-dcv2-metric="jurisdictions"]').forEach((node) => { node.textContent = String(jurisdictions.size); });
      root.querySelectorAll('[data-dcv2-metric="received"]').forEach((node) => { node.textContent = String(received); });
      root.querySelectorAll('[data-dcv2-metric="substantive"]').forEach((node) => { node.textContent = String(substantive); });
      root.querySelectorAll('[data-dcv2-metric="active"]').forEach((node) => { node.textContent = String(active); });
      root.querySelectorAll('[data-dcv2-metric="pilot"]').forEach((node) => { node.textContent = String(pilot); });
      root.querySelectorAll('[data-dcv2-metric="standards"]').forEach((node) => { node.textContent = String(standards); });
      root.querySelectorAll('[data-dcv2-metric="formal"]').forEach((node) => { node.textContent = String(formal); });

      const domainTitleToCode = new Map([
        ['Normalisatie & standaarden','A'],['Accreditatie & certificatieschema’s','B'],['Medische hulpmiddelen & regulatoire route','C'],['Interoperabiliteit & health informatics','D'],['Cybersecurity & zorginformatieveiligheid','E'],['Privacy & data governance','F'],['Toegankelijkheid, senior usability & human factors','G'],['Patiënten, senioren & mantelzorgers','H'],['Zorgprofessionals & sector','I'],['Onderzoek, UMC’s & pilots','J'],['Innovatie, opschaling & financiering','K'],['Zorg-IT leveranciers & sectorgovernance','L'],['Internationale volksgezondheid & ageing','M'],['Identiteit, authenticatie & toegang','N'],['HTA, evidence & uitkomstmeting','O']
      ]);
      domains.forEach((count, title) => {
        const code = domainTitleToCode.get(title);
        if (!code) return;
        root.querySelectorAll(`[data-dcv2-domain-metric="${code}"]`).forEach((node) => { node.textContent = String(count); });
      });

      const regionOrder = ['Nederland','Europa','Noord-Amerika','Zuid-Amerika','Azië','Oceanië','Afrika','International / Global'];
      const regionRoot = root.querySelector('[data-dcv2-regions]');
      if (regionRoot) {
        const fragment = document.createDocumentFragment();
        regionOrder.forEach((region) => {
          const count = regions.get(region) || 0;
          if (!count) return;
          const item = el('div', 'dcv2-region');
          item.append(el('strong', '', region), el('span', '', `${count} canonieke routes`));
          fragment.append(item);
        });
        regionRoot.replaceChildren(fragment);
      }
      if (status) status.textContent = `${records.length} unieke canonieke routes geladen; tellingen zijn rechtstreeks uit de routedataset berekend.`;
    } catch (error) {
      if (status) status.textContent = 'Actuele routedataset kon tijdelijk niet worden geladen; statische claims worden daarom niet automatisch opgewaardeerd.';
    }
  };

  const domainRoot = document.querySelector('[data-dcv2-domain]');
  const hubRoot = document.querySelector('[data-dcv2-hub]');
  if (domainRoot) initDomain(domainRoot);
  if (hubRoot) initHub(hubRoot);
})();
